import { type PhysicsTuning, TRACK } from '../config/tuning';
import type { CarState } from '../sim/car';
import type { InputFrame } from '../sim/input';
import type { TrackRuntime } from '../sim/trackQuery';
import { spawnState, stepWorld } from '../sim/world';
import { type BotPlan, botInput, makePlan, trackPosition } from '../track/bot';
import type { GeneratedCircuit } from '../track/generate';
import { Surface, type TrackData } from '../track/types';

/**
 * The driving lesson's coach: pure logic, no DOM and no rendering. It reads
 * the car once per sim tick and says when to freeze the game ("brake now",
 * "keep braking"), when an attempt at turn 1 failed and why, and when it
 * passed. Everything here is practice-only: it never touches the simulation,
 * the recording or any hash.
 *
 * Speeds are m/s and positions are fractional centreline sample indices (the
 * index space of the racing line and the bot's speed profile; sample 0 is the
 * start/finish line, samples are about 2 m apart).
 */

/**
 * The beginner plan's grip: the bot's own speed profile at 85% of the grip,
 * so it brakes earlier and takes corners slower than the bot (which drives at
 * 90% to 105%). A new driver who matches it has room to spare.
 */
export const LESSON_GRIP = 0.85;

/**
 * The top of the green zone: the bot's profile at 110% of the grip, about the
 * most the car carries through turn 1 (the bot driving it ran wide on 1 day in
 * 30; at 120% on about half), and what a good driver feels it takes. The
 * beginner speed is the suggestion; anywhere between it and this is fine, and
 * the coach only stops you ("brake now", "keep braking", the flash) over it.
 * A fail still measures "too fast" against the beginner speed, and says that.
 */
export const LESSON_TOP_GRIP = 1.1;
/**
 * ...but braking for it as the car really can: its brakes at 95% (the pedal takes a moment to build, and
 * holding the steering while braking takes some of it). Braking at the full 110% too, "brake now" came too
 * late: braking the moment it said, a keyboard player holding the turn key couldn't make turn 1.
 */
export const LESSON_TOP_BRAKE = 0.95;
/**
 * Lower tops, tried in turn on a circuit whose turn 1 the plan reads too kindly (downhill, say): there the
 * bot driving the top at LESSON_TOP_GRIP runs wide, and a green bar would send a player off.
 */
const TOP_FALLBACK = [1.05, 1];

/** A speed drop this big (m/s) into a stop is "turn 1": heavy braking, not a lift. */
const FIRST_TURN_DROP = 18;
/** Smaller drops than this (m/s) aren't braking zones worth a cue. */
const MIN_DROP = 10;
/**
 * Hysteresis for reading the profile's peaks and troughs (m/s): a rise
 * smaller than this between two falls is one long stop, not two. It merges
 * the plateau of a sweeper that leads into a hairpin into one braking event.
 */
const HYSTERESIS = 2;
/** A peak or trough is where the profile first comes within this of its extreme (m/s), so wiggles don't move it. */
const EXTREME_EPS = 0.1;
/** The pass line sits this far past the end of the corner (m). */
const EXIT_METRES = 30;
/** With no track corner at the bottom of a stop, the corner is taken as this many samples either side. */
const FALLBACK_HALF = 10;
/** A track corner this close (samples) to the bottom of a stop still counts as its corner. */
const CORNER_REACH = 15;

/** Over the zone's top by more than this (m/s), having braked and let go, is too fast: "keep braking". */
const TOL = 1.5;
/**
 * Never having braked, "brake now" waits for a little more (m/s): about a
 * quarter of a second past the point, room for a late foot but not for none.
 */
const BRAKE_TOL = 2.5;
/** Brake input at or above this counts as braking (0..1). */
const BRAKING = 0.4;
/** Brake input below this counts as letting go (0..1); in between is neither. */
const RELEASED = 0.2;
/** Brake input that resumes the game after a freeze (0..1). */
const RESUME = 0.5;
/**
 * The braking window, and the "brake" caption with it, opens this many
 * samples (about 20 m, a third of a second at speed) before the braking
 * point: time to react before "brake now" freezes the game.
 */
const WINDOW_LEAD = 10;
/**
 * A plateau inside a stop: at least this many samples in a row where the
 * profile falls less than FLAT_DROP m/s over the next PLATEAU samples. The fall
 * after it is a new stage if it still has STAGE_DROP m/s to go.
 */
const PLATEAU = 12;
const FLAT_DROP = 1;
const STAGE_DROP = 5;
/** Within this of the stage's top speed (m/s) the braking is done and the bar is green: let go. */
const RELEASE_MARGIN = 1;
/** Once green, the bar stays green until this much faster again (m/s): a car hovering at the line doesn't blink it. */
const GREEN_HOLD = 0.5;
/** Green, "turn" comes once the stage's corner is this close (s at the current speed); before that it's "let go and roll". */
const TURN_LEAD = 1.2;
/** Throttle above this (0..1) is back on the gas. */
const GAS_ON = 0.3;
/** A fail more than this over the beginner profile (m/s), where the speed was measured, was down to speed. */
const TOO_FAST = 2.5;
/** Past the bottom of the stop, "gas" once the profile has risen this much (m/s) above it. */
const GAS_RISE = 1.5;
/** A barrier hit faster than this (m/s, the normal speed into the wall) ends the attempt. */
const WALL_IMPACT = 7;
/** Slower than this (m/s) for STOP_TICKS ticks in a row is stopped. */
const STOP_SPEED = 1.5;
const STOP_TICKS = 40;
/** More than this many samples "ahead" of the spawn is really a car rolling back behind it. */
const BEHIND = 60;
/** Facing further than this from the direction of the track is a spin (radians, about 100 degrees). */
const FACING_BACK = (100 * Math.PI) / 180;

export function beginnerPlan(circuit: GeneratedCircuit, physics: PhysicsTuning): BotPlan {
  return makePlan(circuit.line, physics, LESSON_GRIP);
}

/**
 * The green zone's top: the same, at LESSON_TOP_GRIP, or lower (TOP_FALLBACK) where the bot driving it
 * doesn't make turn 1.
 */
export function topPlan(circuit: GeneratedCircuit, physics: PhysicsTuning): BotPlan {
  // makePlan brakes at the grip level times the car's brakes: scaled so the top plan brakes at LESSON_TOP_BRAKE.
  const at = (grip: number) => makePlan(circuit.line, { ...physics, brakeForce: (physics.brakeForce * LESSON_TOP_BRAKE) / grip }, grip);
  const beginner = beginnerPlan(circuit, physics).profile;
  const turn = firstTurn(circuit.track, beginner);
  const spawn = spawnIndex(circuit.track);
  let top = at(LESSON_TOP_GRIP);
  for (const grip of TOP_FALLBACK) {
    if (makesTurnOne(circuit, physics, turn, beginner, spawn, top)) break;
    top = at(grip);
  }
  return top;
}

/** The bot driving a top plan from the spawn, coached with it: does it pass turn 1? */
function makesTurnOne(circuit: GeneratedCircuit, physics: PhysicsTuning, turn: LessonTurn, beginner: number[], spawn: number, top: BotPlan): boolean {
  const st = spawnState(circuit.runtime, physics);
  const coach = new TurnCoach(turn, beginner, spawn, circuit.track.n, 'guided', top.profile);
  for (let t = 0; t < 60 * 40; t++) {
    const f = botInput(st.car, circuit.runtime, top, physics);
    stepWorld(st, f, circuit.runtime, physics, null);
    const e = coach.step(observe(st.car, circuit.runtime, f));
    if (e) return e.type === 'pass';
  }
  return false;
}

export interface LessonTurn {
  /** Sample where the beginner profile starts falling into this stop. */
  brakeIndex: number;
  /** Sample where it bottoms out (about the apex): the target point. */
  minIndex: number;
  /** The track corner containing minIndex (fallback: minIndex +/- 10). */
  cornerStart: number;
  cornerEnd: number;
  /** About 30 m after cornerEnd (or the next stop's braking point, if that comes first): the pass line. */
  exitIndex: number;
  /** +1 left, -1 right. */
  dir: 1 | -1;
  /** Profile at brakeIndex (m/s). */
  entrySpeed: number;
  /** Profile at minIndex (m/s). */
  targetSpeed: number;
  /**
   * The track corners on the way down (brakeIndex to minIndex, this one
   * included). A stop braked in stages turns in at a bend of its own before
   * this corner: a fast sweeper that leads into a hairpin.
   */
  bends?: { start: number; end: number }[];
}

function wrap(i: number, n: number): number {
  const r = i % n;
  return r < 0 ? r + n : r;
}

/** Profile interpolated at a fractional sample index (wrapping). */
export function profileAt(profile: number[], pos: number): number {
  const n = profile.length;
  const x = wrap(pos, n);
  const i = Math.floor(x);
  const j = i + 1 === n ? 0 : i + 1;
  return profile[i] + (profile[j] - profile[i]) * (x - i);
}

/** The centreline sample the car spawns at (where "restart" drops it). */
export function spawnIndex(track: TrackData): number {
  const s = track.length - track.spawnDistance;
  let best = 0;
  for (let i = 1; i < track.n; i++) if (Math.abs(track.s[i] - s) < Math.abs(track.s[best] - s)) best = i;
  return best;
}

/** Whether sample i lies in the (possibly wrapping) range start..end. */
function inRange(i: number, start: number, end: number): boolean {
  return start <= end ? i >= start && i <= end : i >= start || i <= end;
}

/** Samples from a to b going forward round the lap. */
function ahead(a: number, b: number, n: number): number {
  return wrap(b - a, n);
}

/** The corner containing sample i or, failing that, the nearest one within CORNER_REACH samples. */
function cornerAround<T extends { start: number; end: number }>(corners: readonly T[], i: number, n: number): T | undefined {
  let corner = corners.find((k) => inRange(i, k.start, k.end));
  if (corner) return corner;
  // A stop can bottom out just before or after the corner's own samples.
  let bestGap = CORNER_REACH + 1;
  for (const k of corners) {
    const gap = Math.min(ahead(i, k.start, n), ahead(k.end, i, n));
    if (gap < bestGap) {
      bestGap = gap;
      corner = k;
    }
  }
  return corner;
}

function makeTurn(track: TrackData, profile: number[], brakeIndex: number, minIndex: number): LessonTurn {
  const n = track.n;
  const corner = cornerAround(track.corners, minIndex, n);
  const bends = track.corners
    .filter((k) => inRange(k.start, brakeIndex, minIndex) || inRange(brakeIndex, k.start, k.end))
    .map((k) => ({ start: k.start, end: k.end }));
  const cornerStart = corner ? corner.start : wrap(minIndex - FALLBACK_HALF, n);
  const cornerEnd = corner ? corner.end : wrap(minIndex + FALLBACK_HALF, n);
  const dir: 1 | -1 = corner ? (corner.dir > 0 ? 1 : -1) : track.curvature[minIndex] >= 0 ? 1 : -1;
  // The pass line is past both the corner and the bottom of the stop.
  const end = ahead(minIndex, cornerEnd, n) < n / 2 ? cornerEnd : minIndex;
  const exitIndex = wrap(end + Math.round(EXIT_METRES / (track.length / n)), n);
  return {
    brakeIndex,
    minIndex,
    cornerStart,
    cornerEnd,
    exitIndex,
    dir,
    entrySpeed: profile[brakeIndex],
    targetSpeed: profile[minIndex],
    bends,
  };
}

/**
 * Every braking event round the lap (drop >= minDrop m/s), in track order
 * from sample 0 (by minIndex). An event is a falling stretch of the profile,
 * from the peak before it to the trough after it; rises smaller than
 * HYSTERESIS on the way down don't split it. An event's pass line comes no
 * later than the next event's braking point.
 */
export function brakingEvents(track: TrackData, profile: number[], minDrop = MIN_DROP): LessonTurn[] {
  const n = profile.length;
  // Start at the fastest sample: it is always a peak.
  let top = 0;
  for (let i = 1; i < n; i++) if (profile[i] > profile[top]) top = i;
  const legs: [number, number][] = [];
  let peak = top;
  let trough = top;
  let falling = true;
  for (let k = 1; k <= n; k++) {
    const i = (top + k) % n;
    const v = profile[i];
    if (falling) {
      if (v < profile[trough]) trough = i;
      else if (v > profile[trough] + HYSTERESIS) {
        legs.push([peak, trough]);
        falling = false;
        peak = i;
      }
    } else if (v >= profile[peak]) peak = i;
    else if (v < profile[peak] - HYSTERESIS) {
      falling = true;
      trough = i;
    }
  }
  if (falling && ahead(peak, trough, n) > 0) legs.push([peak, trough]);

  const out: LessonTurn[] = [];
  for (const [p, t] of legs) {
    const len = ahead(p, t, n);
    if (profile[p] - profile[t] < minDrop || len === 0) continue;
    // The braking point is the last sample still near the top; the target the first near the bottom.
    let brakeIndex = p;
    let minIndex = t;
    for (let k = 0; k <= len; k++) {
      const i = (p + k) % n;
      if (profile[i] >= profile[p] - EXTREME_EPS) brakeIndex = i;
      else break;
    }
    for (let k = 0; k <= len; k++) {
      const i = (p + k) % n;
      if (profile[i] <= profile[t] + EXTREME_EPS) {
        minIndex = i;
        break;
      }
    }
    out.push(makeTurn(track, profile, brakeIndex, minIndex));
  }
  out.sort((a, b) => a.minIndex - b.minIndex);
  // The pass line never runs into the next stop's braking: that one is coached on its own.
  for (let k = 0; k < out.length && out.length > 1; k++) {
    const e = out[k];
    const next = out[(k + 1) % out.length];
    if (ahead(e.minIndex, next.brakeIndex, n) < ahead(e.minIndex, e.exitIndex, n)) {
      e.exitIndex = ahead(e.minIndex, next.brakeIndex, n) > 0 ? next.brakeIndex : (e.minIndex + 1) % n;
    }
  }
  return out;
}

/**
 * The first braking event with a drop of at least 18 m/s met after the spawn
 * (walking forward from the spawn sample, wrapping): the lesson's turn 1.
 */
export function firstTurn(track: TrackData, profile: number[]): LessonTurn {
  const n = track.n;
  const spawn = spawnIndex(track);
  const events = brakingEvents(track, profile);
  const drop = (e: LessonTurn): number => e.entrySpeed - e.targetSpeed;
  const byDistance = (a: LessonTurn, b: LessonTurn): number => ahead(spawn, a.minIndex, n) - ahead(spawn, b.minIndex, n);
  let pick = events.filter((e) => drop(e) >= FIRST_TURN_DROP && e.minIndex !== spawn).sort(byDistance)[0];
  // Every circuit has heavy braking somewhere; failing that, take the biggest stop.
  if (!pick) pick = events.slice().sort((a, b) => drop(b) - drop(a))[0];
  if (!pick) {
    let lo = 0;
    for (let i = 1; i < n; i++) if (profile[i] < profile[lo]) lo = i;
    return makeTurn(track, profile, spawn, lo);
  }
  // A spawn inside the braking zone brakes from the spawn.
  if (ahead(spawn, pick.brakeIndex, n) > ahead(spawn, pick.minIndex, n)) return { ...pick, brakeIndex: spawn, entrySpeed: profile[spawn] };
  return pick;
}

export type Level = 'guided' | 'own' | 'free'; // free = rest-of-lap cues only (never freezes, never fails)

export interface Observation {
  /** trackPosition(car, rt). */
  pos: number;
  /** m/s, hypot(vx, vy). */
  speed: number;
  /** 0..1 input this tick. */
  throttle: number;
  /** 0..1 input this tick (what the sim was fed, so a brake assist counts). */
  brake: number;
  /** All four wheels on surface >= 2. */
  offTrack: boolean;
  /** Heading more than about 100 degrees away from the track direction. */
  facingBack: boolean;
  /** car.barrierImpact this tick (m/s). */
  impact: number;
}

/** What the coach needs from the car after a tick, given the frame the sim was fed on it. */
export function observe(car: CarState, rt: TrackRuntime, input: InputFrame): Observation {
  const sf = car.surfaces;
  const i = car.trackIndex;
  const along = rt.tx[i] * Math.cos(car.heading) + rt.ty[i] * Math.sin(car.heading);
  return {
    pos: trackPosition(car, rt),
    speed: Math.hypot(car.vx, car.vy),
    throttle: input.throttle / 255,
    brake: input.brake / 255,
    offTrack: sf[0] >= Surface.Grass && sf[1] >= Surface.Grass && sf[2] >= Surface.Grass && sf[3] >= Surface.Grass,
    facingBack: along < Math.cos(FACING_BACK),
    impact: car.barrierImpact,
  };
}

export type FailReason = 'wide' | 'wall' | 'spin' | 'stopped';

export type CoachEvent =
  | {
      type: 'freeze';
      why: 'brake' | 'keep';
      speed: number;
      /** The speed to get under: the current stage's top (m/s). */
      target: number;
      /** The target is the turn's own, not an earlier stage's ("slow to 184 first"). */
      final: boolean;
    }
  | {
      type: 'fail';
      why: FailReason;
      /** Speed at cornerStart (or the bottom of the stop, if that comes first), m/s. */
      arrived: number | null;
      /** The beginner speed at the corner (m/s): what to aim for next time. */
      target: number;
      braked: boolean;
      /** The fail came before the braking window: the player never reached the red. */
      early: boolean;
      /** Speed at the fail (going into the wall, for a wall hit), m/s. */
      speed: number;
      /** Over the beginner profile by more than 2.5 m/s where it was measured: at the corner if arrived, else at the fail. */
      tooFast: boolean;
    }
  | { type: 'pass'; arrived: number | null };

/**
 * approach: before the braking window. brake: in it and slower still to go.
 * keep: let go before the bar went green. release: green, the corner still
 * well ahead ("let go, roll to the corner"). turn: green and the stage's
 * corner is near, or in it; past the bottom, easing off. gas: past the
 * bottom, the corner opening up. done: past the pass line.
 */
export type Phase = 'approach' | 'brake' | 'keep' | 'release' | 'turn' | 'gas' | 'done';

export interface CoachDisplay {
  phase: Phase;
  /** The "brake!" flash: own/free level, over the zone's top and not braking (or back on the gas after the bar went green). */
  flash: boolean;
  /**
   * m/s; target = the beginner speed (the suggestion), top = the most that's still fine (green from there
   * down); start = speed when braking began; final = the target is the turn's own. Shown in the braking
   * window only.
   */
  bar: { speed: number; target: number; top: number; start: number; ok: boolean; final: boolean } | null;
}

/** How much of the slowing the bar shows as done, 0..1. */
export function barFill(bar: { speed: number; target: number; start: number }): number {
  const need = bar.start - bar.target;
  if (need <= 0) return bar.speed <= bar.target ? 1 : 0;
  const f = (bar.start - bar.speed) / need;
  return f < 0 ? 0 : f > 1 ? 1 : f;
}

/** Where the green zone starts along the bar (0 to 1): the top's place between the start and the target. */
export function barZone(bar: { target: number; top: number; start: number }): number {
  const need = bar.start - bar.target;
  if (need <= 0) return 1;
  const z = (bar.start - bar.top) / need;
  return z < 0 ? 0 : z > 1 ? 1 : z;
}

/**
 * Coaches one attempt at one braking event, fed an Observation after every
 * sim tick. Progress is counted in samples from the spawn (or wherever the
 * coach took over); the attempt runs from there to the event's pass line.
 *
 * - The braking window runs from 10 samples before the braking point to the
 *   bottom of the stop. The beginner profile is the suggestion (the bar's
 *   target); a second, quicker profile is the green zone's top, and only over
 *   that does the coach step in (a fail still says "too fast" against the
 *   beginner speed):
 *   never having braked (by BRAKE_TOL), that's "brake now" (a freeze at the
 *   guided level, a flash otherwise); having braked and let go before the bar
 *   went green (by TOL), it's "keep braking" (a freeze at the guided and own
 *   levels, a caption when free); back on the gas after the bar went green
 *   (by TOL), it's "brake" again (a freeze at the guided level, a flash
 *   otherwise), since letting go was right and the gas was not. A freeze's
 *   target is the speed the current stage slows to.
 * - A stop with a plateau in it (a fast sweeper that leads into a hairpin) is
 *   braked for in stages, each with its own target and its own corner: slow
 *   for the sweeper, hold it, then brake again for the hairpin. "Never
 *   braked", the green bar and the bar's start begin again with each stage.
 * - A freeze doesn't come again for the same reason until the player brakes
 *   (and resuming takes a press of the brake).
 * - Running wide, spinning, hitting a wall or stopping ends a guided or own
 *   attempt anywhere before the pass line; reaching the pass line passes it
 *   (at every level: a free coach's pass says to move on to the next stop).
 * - The captions: approach, then in the window brake until the bar is green
 *   (keep, if they let go too soon), then let go and roll to the corner, and
 *   turn once it's about a second away. Past the bottom, gas once the corner
 *   opens up, easing off ("turn") whenever the car gets ahead of the profile.
 */
export class TurnCoach {
  readonly level: Level;
  private readonly profile: number[];
  /** The green zone's top, a quicker profile: speeds between the beginner profile and this are fine. */
  private readonly top: number[];
  private readonly turn: LessonTurn;
  private readonly spawn: number;
  private readonly n: number;
  // Progress (samples from the spawn) of the event's landmarks.
  private readonly pWindow: number;
  private readonly pMin: number;
  private readonly pCorner: number;
  private readonly pExit: number;
  /** Where each stage's coaching begins (progress), the speed it slows to (m/s) and where its corner starts (progress). */
  private readonly stageStart: number[] = [];
  private readonly stageTarget: number[] = [];
  /** Each stage's most that's still fine (m/s): the bar goes green under it. */
  private readonly stageTop: number[] = [];
  private readonly stageCorner: number[] = [];
  private stage = 0;
  /** Braked at least once in the window (any stage). */
  private braked = false;
  /** Braked in the current stage. */
  private stageBraked = false;
  /** The bar went green in the current stage. */
  private stageOk = false;
  /** Speed when braking began in the current stage (the bar's start). */
  private startSpeed: number | null = null;
  /** Speed at the corner (or the bottom of the stop, if that comes first), and how far over the profile it was there. */
  private arrived: number | null = null;
  private arrivedOver = 0;
  private brakeArmed = true;
  private keepArmed = true;
  private gasArmed = true;
  private slowTicks = 0;
  private lastP = -Infinity;
  /** The previous tick's speed and position (what a wall hit was going in at). */
  private lastSpeed: number | null = null;
  private lastPos = 0;
  /** Past the bottom, faster than the profile: ease off rather than gas (until back under it). */
  private easeOff = false;
  /** Past the bottom, the corner has opened up: gas from here on (a dip in a long corner doesn't close it again). */
  private opened = false;
  private ended = false;
  /** The stop was already behind when the coach took over (the last corner's exit ran past it). */
  private readonly past: boolean;

  /** `top`: the green zone's top (without one, the zone is just the beginner speed). */
  constructor(turn: LessonTurn, profile: number[], spawnIndex: number, n: number, level: Level, top: number[] = profile) {
    this.turn = turn;
    this.profile = profile;
    this.top = top;
    this.spawn = spawnIndex;
    this.n = n;
    this.level = level;
    this.pMin = ahead(spawnIndex, turn.minIndex, n);
    this.past = this.pMin > n - BEHIND;
    this.pExit = Math.max(ahead(spawnIndex, turn.exitIndex, n), this.pMin + 1);
    // Taking over inside a braking zone (or a corner): it began at the spawn.
    const brake = ahead(spawnIndex, turn.brakeIndex, n);
    const from = brake <= this.pMin ? turn.brakeIndex : spawnIndex;
    const pFrom = ahead(spawnIndex, from, n);
    this.pWindow = pFrom - WINDOW_LEAD;
    // A corner that starts behind the spawn is one the car is already in.
    const cornerAt = (start: number, bottom: number): number => {
      const p = ahead(spawnIndex, start, n);
      return p > this.pExit ? 0 : Math.min(p, bottom);
    };
    this.pCorner = cornerAt(turn.cornerStart, this.pMin);

    // Stages: a new one starts where the profile falls again after a plateau (seen PLATEAU samples ahead).
    const len = this.pMin - pFrom;
    const at = (k: number): number => profile[(from + k) % n];
    const flat = (k: number): boolean => k + PLATEAU <= len && at(k) - at(k + PLATEAU) < FLAT_DROP;
    const cuts = [0];
    let flatRun = 0;
    for (let k = 0; k < len; k++) {
      if (flat(k)) flatRun++;
      else {
        if (flatRun >= PLATEAU && at(k) - turn.targetSpeed >= STAGE_DROP) cuts.push(k);
        flatRun = 0;
      }
    }
    for (let s = 0; s < cuts.length; s++) {
      const last = s + 1 === cuts.length;
      const end = last ? len : cuts[s + 1];
      let lo = Infinity;
      for (let k = cuts[s]; k <= end; k++) lo = Math.min(lo, at(k));
      this.stageStart.push(s === 0 ? this.pWindow : pFrom + cuts[s]);
      this.stageTarget.push(last ? turn.targetSpeed : lo);
      // The top over the same stretch (the turn's own reaches a little past the bottom: a quicker line's apex comes later).
      let hi = Infinity;
      for (let k = cuts[s]; k <= (last ? len + FALLBACK_HALF : end); k++) hi = Math.min(hi, top[(from + k) % n]);
      this.stageTop.push(Math.max(this.stageTarget[s], hi));
      if (last) this.stageCorner.push(this.pCorner);
      else {
        // An earlier stage turns in at the bend where it bottoms out (the plateau's own corner).
        let bottom = cuts[s];
        while (bottom < end && at(bottom) > lo + EXTREME_EPS) bottom++;
        const i = (from + bottom) % n;
        const bend = cornerAround(turn.bends ?? [], i, n);
        this.stageCorner.push(cornerAt(bend ? bend.start : wrap(i - FALLBACK_HALF, n), pFrom + bottom));
      }
    }
  }

  /** Samples from the spawn to a car position; negative behind the spawn. */
  private prog(pos: number): number {
    const p = wrap(pos - this.spawn, this.n);
    return p > this.n - BEHIND ? p - this.n : p;
  }

  private stageAt(p: number): number {
    let s = 0;
    while (s + 1 < this.stageStart.length && p >= this.stageStart[s + 1]) s++;
    return s;
  }

  /** Past the bottom, the profile has risen enough for the gas. */
  private opensUp(pos: number): boolean {
    return profileAt(this.profile, pos) >= this.turn.targetSpeed + GAS_RISE;
  }

  /** The stage's target is the turn's own (the last stage's). */
  private isFinal(stage: number): boolean {
    return stage === this.stageTarget.length - 1;
  }

  /** Call after every sim tick. Returns an event when the attempt freezes, fails or passes. */
  step(o: Observation): CoachEvent | null {
    if (this.ended) return null;
    if (this.past) {
      this.ended = true;
      return { type: 'pass', arrived: null };
    }
    const p = this.prog(o.pos);
    const braking = o.brake >= BRAKING;
    const inWindow = p >= this.pWindow && p < this.pMin;
    const stage = this.stageAt(p);
    if (stage !== this.stage) {
      this.stage = stage;
      this.stageBraked = false;
      this.stageOk = false;
      this.startSpeed = null;
      this.brakeArmed = true;
      this.keepArmed = true;
      this.gasArmed = true;
    }
    if (braking) {
      this.keepArmed = true;
      this.gasArmed = true;
      if (inWindow && !this.stageBraked) {
        this.braked = true;
        this.stageBraked = true;
        this.startSpeed = o.speed;
      }
    }
    if (inWindow && o.speed <= this.stageTop[stage] + RELEASE_MARGIN) this.stageOk = true;
    if (this.arrived === null && p >= this.pCorner) {
      this.arrived = o.speed;
      this.arrivedOver = o.speed - profileAt(this.profile, o.pos);
    }

    if (this.level !== 'free') {
      // Crawling, or rolling backwards (holding the brake at a standstill engages reverse).
      this.slowTicks = o.speed < STOP_SPEED || p < this.lastP ? this.slowTicks + 1 : 0;
      const why: FailReason | null =
        o.impact > WALL_IMPACT ? 'wall' : o.facingBack ? 'spin' : o.offTrack ? 'wide' : this.slowTicks >= STOP_TICKS ? 'stopped' : null;
      if (why) {
        this.ended = true;
        const early = p < this.pWindow;
        // A wall hit is reported at the speed going into it, not what was left after it.
        const into = why === 'wall' && this.lastSpeed !== null && this.lastSpeed > o.speed ? this.lastSpeed : null;
        const speed = into ?? o.speed;
        const pos = into !== null ? this.lastPos : o.pos;
        const tooFast = this.arrived !== null ? this.arrivedOver > TOO_FAST : !early && speed - profileAt(this.profile, pos) > TOO_FAST;
        return { type: 'fail', why, arrived: this.arrived, target: this.turn.targetSpeed, braked: this.braked, early, speed, tooFast };
      }
    }
    this.lastP = p;
    this.lastSpeed = o.speed;
    this.lastPos = o.pos;
    if (p >= this.pExit) {
      this.ended = true;
      return { type: 'pass', arrived: this.arrived };
    }
    const over = o.speed - profileAt(this.top, o.pos);
    if (p >= this.pMin) {
      this.easeOff = over > TOL || (this.easeOff && over > 0);
      if (this.opensUp(o.pos)) this.opened = true;
    }
    if (!inWindow || this.level === 'free' || over <= TOL) return null;
    const target = this.stageTop[stage];
    const final = this.isFinal(stage);
    if (this.stageOk) {
      // Back on the gas after the bar went green: letting go was right, the gas wasn't. "Brake" again.
      if (this.level === 'guided' && this.gasArmed && !braking && o.throttle > GAS_ON) {
        this.gasArmed = false;
        return { type: 'freeze', why: 'brake', speed: o.speed, target, final };
      }
    } else if (!this.stageBraked) {
      if (this.level === 'guided' && this.brakeArmed && over > BRAKE_TOL) {
        this.brakeArmed = false;
        return { type: 'freeze', why: 'brake', speed: o.speed, target, final };
      }
    } else if (o.brake < RELEASED && this.keepArmed) {
      this.keepArmed = false;
      return { type: 'freeze', why: 'keep', speed: o.speed, target, final };
    }
    return null;
  }

  /** While frozen: may the sim resume? (brake pressed >= 0.5) */
  canResume(brake: number): boolean {
    return brake >= RESUME;
  }

  display(o: Observation): CoachDisplay {
    const p = this.prog(o.pos);
    if (this.ended || this.past || p >= this.pExit) return { phase: 'done', flash: false, bar: null };
    // Past the bottom: gas once the corner opens up (a long corner holds its speed a while first),
    // easing off whenever the car gets ahead of the profile.
    if (p >= this.pMin) {
      const open = this.opened || this.opensUp(o.pos);
      return { phase: open && !this.easeOff ? 'gas' : 'turn', flash: false, bar: null };
    }
    if (p < this.pWindow) return { phase: 'approach', flash: false, bar: null };
    const stage = this.stageAt(p);
    const current = stage === this.stage;
    const stageBraked = current && this.stageBraked;
    const stageOk = current && this.stageOk;
    const t = this.stageTarget[stage];
    const top = this.stageTop[stage];
    const braking = o.brake >= BRAKING;
    const over = o.speed - profileAt(this.top, o.pos);
    const ok = o.speed <= top + RELEASE_MARGIN + (stageOk ? GREEN_HOLD : 0);
    let phase: Phase;
    if (ok) {
      // Green: let go and roll to the stage's corner, and turn in once it's near (or already in it).
      const toCorner = ((this.stageCorner[stage] - p) * TRACK.sampleSpacing) / Math.max(o.speed, STOP_SPEED);
      phase = toCorner <= TURN_LEAD ? 'turn' : 'release';
    }
    // Let go too soon: still too fast with the bar not yet green.
    else if (stageBraked && !stageOk && o.brake < RELEASED && over > TOL) phase = 'keep';
    // In the window and slower still to go: brake, whatever else is going on, until the bar is green.
    else phase = 'brake';
    const flash =
      this.level !== 'guided' &&
      !braking &&
      (stageOk ? o.throttle > GAS_ON && over > TOL : over > (stageBraked ? TOL : BRAKE_TOL));
    const start = current && this.startSpeed !== null ? this.startSpeed : Math.max(o.speed, t);
    return { phase, flash, bar: { speed: o.speed, target: t, top, start, ok, final: this.isFinal(stage) } };
  }
}

/**
 * The lesson's fading help. The first pass is guided; then one attempt on
 * your own. Two own fails in a row go back to guided; five fails in all bring
 * the instructor's demo, and two more after it switch on the brake assist
 * until a pass.
 */
export class LessonProgress {
  level: Level = 'guided';
  /** Attempts at turn 1 so far. */
  tries = 0;
  fails = 0;
  demoShown = false;
  /** The hidden brake assist is on. */
  assist = false;
  private ownFails = 0;
  private demoFails = 0;

  startAttempt(): void {
    this.tries++;
  }

  /** guided -> own; own (or assisted) -> mastered, and the rest of the lap is free. */
  onPass(): 'own' | 'mastered' {
    if (this.level === 'guided' && !this.assist) {
      this.level = 'own';
      this.ownFails = 0;
      return 'own';
    }
    this.level = 'free';
    return 'mastered';
  }

  /** 'demo' when the fails reach five and there's been no demo yet; after it, two more fails switch on the assist. */
  onFail(): 'retry' | 'demo' {
    this.fails++;
    if (this.level === 'own' && ++this.ownFails >= 2) {
      this.level = 'guided';
      this.ownFails = 0;
    }
    if (!this.demoShown) return this.fails >= 5 ? 'demo' : 'retry';
    if (++this.demoFails >= 2) this.assist = true;
    return 'retry';
  }

  demoDone(): void {
    this.demoShown = true;
    this.level = 'guided';
    this.ownFails = 0;
  }
}
